/** Persisted launcher coordinates after user drag (viewport px). */
export interface HeyNowLauncherPosition {
  top: number;
  left: number;
}

export const HEYNOW_LAUNCHER_POSITION_CLASS = 'bqm-heynow-user-positioned';
export const HEYNOW_LAUNCHER_POSITION_STORAGE_KEY = 'bqm-heynow-launcher-position';
export const HEYNOW_LAUNCHER_TOP_VAR = '--bqm-heynow-launcher-top';
export const HEYNOW_LAUNCHER_LEFT_VAR = '--bqm-heynow-launcher-left';

const VIEWPORT_MARGIN_PX = 8;
const LAUNCHER_FALLBACK_SIZE_PX = 56;
const IOS_CONTENT_TOP_FALLBACK_PX = 112;
const IOS_TAB_BAR_FALLBACK_PX = 70;

function parseCssLengthPx(value: string): number {
  const px = Number.parseFloat(value.trim());
  return Number.isFinite(px) ? px : 0;
}

function isNativeMobileContext(): boolean {
  return document.documentElement.classList.contains('plt-hybrid');
}

function iosLauncherVerticalBounds(
  size: { height: number },
  viewport: { height: number }
): { minTop: number; maxTop: number } {
  const rootStyle = getComputedStyle(document.documentElement);
  const contentTop =
    parseCssLengthPx(rootStyle.getPropertyValue('--app-content-offset-top')) || IOS_CONTENT_TOP_FALLBACK_PX;
  const tabBar = parseCssLengthPx(rootStyle.getPropertyValue('--app-tab-bar-total')) || IOS_TAB_BAR_FALLBACK_PX;
  const minTop = contentTop + VIEWPORT_MARGIN_PX;
  const maxTop = Math.max(minTop, viewport.height - tabBar - size.height - VIEWPORT_MARGIN_PX);
  return { minTop, maxTop };
}

/** Keeps launcher fully visible inside the viewport. */
export function clampHeyNowLauncherToViewport(
  position: HeyNowLauncherPosition,
  size: { width: number; height: number },
  viewport?: { width: number; height: number }
): HeyNowLauncherPosition {
  const vp = viewport ?? { width: globalThis.innerWidth, height: globalThis.innerHeight };
  const maxLeft = Math.max(VIEWPORT_MARGIN_PX, vp.width - size.width - VIEWPORT_MARGIN_PX);
  let minTop = VIEWPORT_MARGIN_PX;
  let maxTop = Math.max(VIEWPORT_MARGIN_PX, vp.height - size.height - VIEWPORT_MARGIN_PX);

  if (isNativeMobileContext()) {
    const iosBounds = iosLauncherVerticalBounds(size, vp);
    minTop = iosBounds.minTop;
    maxTop = iosBounds.maxTop;
  }

  return {
    top: Math.min(Math.max(position.top, minTop), maxTop),
    left: Math.min(Math.max(position.left, VIEWPORT_MARGIN_PX), maxLeft),
  };
}

export function readHeyNowLauncherPosition(storage: Storage = localStorage): HeyNowLauncherPosition | null {
  try {
    const raw = storage.getItem(HEYNOW_LAUNCHER_POSITION_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<HeyNowLauncherPosition>;
    if (typeof parsed.top !== 'number' || typeof parsed.left !== 'number') {
      return null;
    }
    return { top: parsed.top, left: parsed.left };
  } catch {
    return null;
  }
}

export function saveHeyNowLauncherPosition(position: HeyNowLauncherPosition, storage: Storage = localStorage): void {
  try {
    storage.setItem(HEYNOW_LAUNCHER_POSITION_STORAGE_KEY, JSON.stringify(position));
  } catch {
    // Private mode / quota — drag still works for current session via inline styles.
  }
}

function launcherSize(chatRoot: HTMLElement): { width: number; height: number } {
  const rect = chatRoot.getBoundingClientRect();
  if (rect.width > 0 && rect.height > 0) {
    return { width: rect.width, height: rect.height };
  }
  return { width: LAUNCHER_FALLBACK_SIZE_PX, height: LAUNCHER_FALLBACK_SIZE_PX };
}

export function captureHeyNowLauncherPosition(chatRoot: HTMLElement): HeyNowLauncherPosition {
  const rect = chatRoot.getBoundingClientRect();
  return clampHeyNowLauncherToViewport({ top: rect.top, left: rect.left }, { width: rect.width, height: rect.height });
}

/** Clears launcher inline positioning so expanded panel / vendor CSS can apply. */
export function clearHeyNowLauncherPositionStyles(chatRoot: HTMLElement): void {
  chatRoot.classList.remove(HEYNOW_LAUNCHER_POSITION_CLASS);
  for (const prop of ['position', 'top', 'left', 'bottom', 'right', 'transform'] as const) {
    chatRoot.style.removeProperty(prop);
  }
  document.documentElement.style.removeProperty(HEYNOW_LAUNCHER_TOP_VAR);
  document.documentElement.style.removeProperty(HEYNOW_LAUNCHER_LEFT_VAR);
}

/** Applies user-chosen fixed coordinates; overrides default bottom/right CSS. */
export function applyHeyNowLauncherPosition(chatRoot: HTMLElement, position: HeyNowLauncherPosition): void {
  chatRoot.classList.add(HEYNOW_LAUNCHER_POSITION_CLASS);
  chatRoot.style.setProperty('position', 'fixed', 'important');
  chatRoot.style.setProperty('top', `${position.top}px`, 'important');
  chatRoot.style.setProperty('left', `${position.left}px`, 'important');
  chatRoot.style.setProperty('bottom', 'unset', 'important');
  chatRoot.style.setProperty('right', 'unset', 'important');
  chatRoot.style.setProperty('transform', 'none', 'important');
  document.documentElement.style.setProperty(HEYNOW_LAUNCHER_TOP_VAR, `${position.top}px`);
  document.documentElement.style.setProperty(HEYNOW_LAUNCHER_LEFT_VAR, `${position.left}px`);
}

function readInlineLauncherPosition(chatRoot: HTMLElement): HeyNowLauncherPosition | null {
  const top = Number.parseFloat(chatRoot.style.getPropertyValue('top'));
  const left = Number.parseFloat(chatRoot.style.getPropertyValue('left'));
  if (!Number.isFinite(top) || !Number.isFinite(left)) {
    return null;
  }
  return { top, left };
}

function shouldHoldLauncherPosition(chatRoot: HTMLElement): boolean {
  return (
    !chatRoot.classList.contains('hey-expanded') &&
    !chatRoot.classList.contains('hey-dragging') &&
    !chatRoot.classList.contains('bqm-heynow-dragging')
  );
}

/** Re-applies saved coords when vendor clears inline top/left on a user-positioned launcher. */
export function holdHeyNowLauncherPosition(chatRoot: HTMLElement, storage: Storage = localStorage): void {
  if (!shouldHoldLauncherPosition(chatRoot)) {
    return;
  }
  const saved = readHeyNowLauncherPosition(storage);
  if (!saved) {
    return;
  }
  if (readInlineLauncherPosition(chatRoot)) {
    return;
  }
  applyHeyNowLauncherPosition(chatRoot, clampHeyNowLauncherToViewport(saved, launcherSize(chatRoot)));
}

export function restoreHeyNowLauncherPosition(chatRoot: HTMLElement, storage: Storage = localStorage): void {
  const saved = readHeyNowLauncherPosition(storage);
  if (!saved) {
    return;
  }
  applyHeyNowLauncherPosition(chatRoot, clampHeyNowLauncherToViewport(saved, launcherSize(chatRoot)));
}

/**
 * Persists launcher position when HeyNow drag ends (.hey-dragging removed).
 * Restores saved position on attach and after panel collapse.
 */
export function attachHeyNowDragPositionHandlers(chatRoot: HTMLElement, storage: Storage = localStorage): () => void {
  if (!chatRoot.classList.contains('hey-expanded')) {
    restoreHeyNowLauncherPosition(chatRoot, storage);
  }

  let wasDragging = chatRoot.classList.contains('hey-dragging');
  let wasExpanded = chatRoot.classList.contains('hey-expanded');

  const onDragEnd = (): void => {
    if (chatRoot.classList.contains('hey-expanded')) {
      return;
    }
    const position = captureHeyNowLauncherPosition(chatRoot);
    applyHeyNowLauncherPosition(chatRoot, position);
    saveHeyNowLauncherPosition(position, storage);
  };

  const onViewportChange = (): void => {
    if (chatRoot.classList.contains('hey-expanded')) {
      return;
    }
    if (readHeyNowLauncherPosition(storage)) {
      restoreHeyNowLauncherPosition(chatRoot, storage);
    }
  };

  const syncState = (): void => {
    const isExpanded = chatRoot.classList.contains('hey-expanded');
    const isDragging = chatRoot.classList.contains('hey-dragging');

    if (!wasExpanded && isExpanded) {
      clearHeyNowLauncherPositionStyles(chatRoot);
    } else if (wasExpanded && !isExpanded) {
      restoreHeyNowLauncherPosition(chatRoot, storage);
    }

    if (wasDragging && !isDragging && !isExpanded) {
      onDragEnd();
    }

    holdHeyNowLauncherPosition(chatRoot, storage);

    wasDragging = isDragging;
    wasExpanded = isExpanded;
  };

  const observer = new MutationObserver(() => syncState());
  observer.observe(chatRoot, { attributes: true, attributeFilter: ['class', 'style'] });

  globalThis.addEventListener('resize', onViewportChange);
  globalThis.addEventListener('orientationchange', onViewportChange);

  return () => {
    observer.disconnect();
    globalThis.removeEventListener('resize', onViewportChange);
    globalThis.removeEventListener('orientationchange', onViewportChange);
  };
}
